import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import SEO, { getServiceSchema, getBreadcrumbSchema } from '../../components/SEO';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PORTRAITS & PHOTOGRAPHY PRICING PAGE - /pricing/portraits
 * ═══════════════════════════════════════════════════════════════════════════
 */

const PricingPortraits = () => {
  const location = useLocation();

  // Handle hash scroll on page load
  useEffect(() => {
    if (location.hash) {
      const scrollToHash = () => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      
      if (document.readyState === 'complete') {
        setTimeout(scrollToHash, 100);
      } else {
        window.addEventListener('load', () => setTimeout(scrollToHash, 100), { once: true });
      }
      
      const backupTimer = setTimeout(scrollToHash, 800);
      return () => clearTimeout(backupTimer);
    }
  }, [location.hash]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title="Portrait Photography Pricing | Kingman AZ"
        description="Professional portrait photography services in Kingman, Arizona. Private sessions, headshots, couples, pet photography & event coverage. Book your session today."
        path="/pricing/portraits"
        jsonLd={[
          getServiceSchema({
            name: 'Portrait Photography',
            description: 'Professional portrait photography including headshots, private sessions, couples photography, pet portraits, and event coverage.',
            url: 'https://rcstudios.net/pricing/portraits',
            areaServed: 'Arizona',
            priceRange: '$199-$600+'
          }),
          getBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Pricing', path: '/pricing' },
            { name: 'Portraits', path: '/pricing/portraits' }
          ])
        ]}
      />

      {/* Hero Section */}
      <section className="bg-[#050816] text-white pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-[1000px]">
          {/* Back Link */}
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-8 transition-colors"
            data-testid="back-to-categories"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Categories
          </Link>
          
          <div className="text-center">
            <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-4">Portraits & Photography</p>
            <h1 className="font-serif font-semibold text-4xl md:text-5xl mb-6">
              Portrait & Photography Services
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Capturing authentic moments and creating timeless portraits that tell your story.
            </p>
          </div>
        </div>
      </section>

      {/* Private Sessions Section */}
      <section id="private-sessions" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=800"
                alt="Private Sessions"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 text-white">
                <p className="text-[#F5C056] text-xs font-bold uppercase tracking-widest mb-2">By Appointment Only</p>
                <h3 className="font-serif text-2xl font-bold">Private Sessions</h3>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Private Sessions</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Intimate, personalized portrait sessions designed for those who value privacy and exclusivity. 
                Each session is tailored to capture your authentic self in a comfortable, professional environment.
              </p>
              
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-serif font-medium text-gray-900">$500</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {[
                    "2-hour private session",
                    "Professional hair & makeup consultation",
                    "10+ edited high-resolution images",
                    "Private online gallery",
                    "Print release included"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
                  <Link to="/contact">Request Consultation</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Headshots Section */}
      <section id="headshots" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4">Headshots & Portraits</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional headshots for executives, actors, and personal branding.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-[900px] mx-auto">
            {[
              { title: 'Executive Headshot', price: '$199', features: ['1 look', '3 edited images', '30-minute session'] },
              { title: 'Professional Package', price: '$349', features: ['2 looks', '8 edited images', '1-hour session', 'Outfit consultation'] },
              { title: 'Premium Branding', price: '$599', features: ['3+ looks', '15 edited images', '2-hour session', 'Styling included', 'Multiple backgrounds'] },
            ].map((pkg, i) => (
              <div key={i} className="bg-[#FAF9F6] rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-all">
                <h3 className="font-serif text-xl text-gray-900 mb-2">{pkg.title}</h3>
                <p className="text-2xl font-serif font-medium text-gray-900 mb-4">{pkg.price}</p>
                <ul className="space-y-2 mb-6">
                  {pkg.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-1.5"></div>
                      {f}
                    </li>
                  ))}
                </ul>
                <Button asChild variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg">
                  <Link to="/contact">Request a Quote</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Couples Section */}
      <section id="couples" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 order-2 lg:order-1">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Couples & Engagements</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Celebrate your love story with romantic, candid photography that captures the connection between you.
              </p>
              
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-serif font-medium text-gray-900">$350</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {[
                    "1.5-hour session at location of choice",
                    "25+ edited images",
                    "Online gallery with download",
                    "Print-ready files"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
                  <Link to="/contact">Book Session</Link>
                </Button>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3] order-1 lg:order-2">
              <img
                src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=800"
                alt="Couples Photography"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pet Photography Section */}
      <section id="pets" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&q=80&w=800"
                alt="Pet Photography"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Pet Photography</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Capture the personality and spirit of your beloved pets with fun, professional portraits.
              </p>
              
              <div className="bg-[#FAF9F6] rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-serif font-medium text-gray-900">$199</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {[
                    "45-minute session",
                    "10+ edited images",
                    "Indoor or outdoor location",
                    "Multiple pets welcome"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
                  <Link to="/contact">Book Session</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4">Events & Lifestyle</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Documentary-style coverage for parties, gatherings, and special occasions.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <p className="text-sm text-muted-foreground">Starting at</p>
              <p className="text-4xl font-serif font-medium text-gray-900">$300</p>
              <p className="text-sm text-muted-foreground mt-1">for 2 hours</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                "Candid event coverage",
                "Group photos",
                "Detail shots",
                "Online gallery",
                "Quick turnaround",
                "Additional hours available"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                  <span className="text-gray-600 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
              <Link to="/contact">Get a Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Back to Categories Footer */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-6 text-center">
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-[#050816] hover:text-[#F5C056] font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Categories
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingPortraits;
